import { useNavigate } from "react-router-dom";
import {
  Lightbulb, PencilLine, GraduationCap, Rocket, Activity, ArrowRight,
  FlaskConical, Search, TrendingUp, Calculator, Share2, Star, Layers, PieChart, ScanSearch,
  BookOpen, Target, DollarSign, Crown,
} from "lucide-react";
import { usePro } from "../hooks/usePro";

const JOURNEY = [
  { label: "Find a Business Idea", icon: Lightbulb, to: "/business-ideas", tint: "bg-[#FBF7EA] text-[#B8901F]" },
  { label: "I Have an Idea", icon: PencilLine, action: "have_idea", tint: "bg-emerald-50 text-emerald-600" },
  { label: "Build Your Blueprint", icon: GraduationCap, action: "blueprint", tint: "bg-indigo-50 text-indigo-600" },
  { label: "Validate Your Idea", icon: Rocket, to: "/ai-resources/validation-lab", tint: "bg-orange-50 text-orange-500" },
  { label: "Track Your Progress", icon: Activity, to: "/ai-resources/roadmap", tint: "bg-pink-50 text-pink-500" },
];

const TOOLS = [
  { label: "Validation Lab", icon: FlaskConical, to: "/ai-resources/validation-lab", color: "#7c3aed" },
  { label: "Competitor Search", icon: Search, to: "/ai-resources/competitor-search", color: "#0d9488" },
  { label: "Market Size Estimator", icon: TrendingUp, to: "/ai-resources/market-size", color: "#2563eb" },
  { label: "Market Size Calculator", icon: Calculator, to: "/ai-resources/market-size", color: "#ea580c" },
  { label: "AI Workflow", icon: Share2, to: "/ai-resources/workflow", color: "#0891b2" },
  { label: "Tool Recommendations", icon: Star, to: "/ai-resources/tool-recommendations", color: "#D4AF37" },
  { label: "AI Tool Stack Audit", icon: Layers, to: "/ai-resources/tool-stack", color: "#4f46e5" },
  { label: "Business Analyzer", icon: PieChart, to: "/ai-resources", color: "#16a34a" },
  { label: "Name Checker", icon: ScanSearch, to: "/ai-resources/name-checker", color: "#0d9488" },
];

const COURSES = [
  { name: "Startup Foundations", lessons: 12, pct: 80, icon: BookOpen, tint: "bg-indigo-50 text-indigo-600" },
  { name: "Validate Your Idea", lessons: 10, pct: 60, icon: Target, tint: "bg-emerald-50 text-emerald-600" },
  { name: "Build Your MVP", lessons: 15, pct: 40, icon: Rocket, tint: "bg-orange-50 text-orange-500" },
  { name: "Pricing & Monetization", lessons: 8, pct: 20, icon: DollarSign, tint: "bg-[#FBF7EA] text-[#B8901F]" },
];

const barColor = (pct) => (pct >= 70 ? "#22c55e" : pct >= 45 ? "#84cc16" : "#D4AF37");

const Panel = ({ title, subtitle, className = "", children }) => (
  <div className={`rounded-3xl p-6 lg:p-7 flex flex-col ${className}`}>
    <h3 className="font-display text-xl font-extrabold text-[#0F0F0F]">{title}</h3>
    <p className="text-sm text-[#687280] mt-1 leading-snug">{subtitle}</p>
    <div className="mt-6 flex-1 flex flex-col">{children}</div>
  </div>
);

export const HomeHighlights = () => {
  const navigate = useNavigate();

  return (
    <>
      {/* 3-column feature row */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pt-16" data-testid="home-highlights">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Start Your Journey */}
          <Panel title="Start Your Journey" subtitle="Begin your startup journey in just a few steps." className="bg-white border border-gray-100 shadow-[0_4px_24px_rgb(0,0,0,0.04)]">
            <div className="space-y-2.5 flex-1">
              {JOURNEY.map((s, i) => (
                <button
                  key={s.label}
                  onClick={() => s.action ? window.dispatchEvent(new CustomEvent("lbs:open-blueprint", { detail: { mode: s.action } })) : navigate(s.to)}
                  className="w-full flex items-center gap-3 rounded-xl bg-white/70 hover:bg-white px-3 py-2.5 text-left transition-colors group"
                  data-testid={`journey-step-${i}`}
                >
                  <span className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${s.tint}`}><s.icon size={17} /></span>
                  <span className="font-semibold text-sm text-[#0F0F0F] flex-1">{s.label}</span>
                  <ArrowRight size={15} className="text-[#9aa1ab] opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                </button>
              ))}
            </div>
            <button
              onClick={() => window.dispatchEvent(new CustomEvent("lbs:open-blueprint", { detail: { mode: "have_idea" } }))}
              className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#0F0F0F] text-[#D4AF37] rounded-full py-3 text-sm font-bold hover:bg-[#1c1c1c] transition-colors"
              data-testid="journey-start-btn"
            >
              Start Now <ArrowRight size={16} />
            </button>
          </Panel>

          {/* AI Resources & Tools */}
          <Panel title="AI Resources & Tools" subtitle="Powerful AI tools to help you research, validate and build with confidence." className="bg-white border border-gray-100 shadow-[0_4px_24px_rgb(0,0,0,0.04)]">
            <div className="grid grid-cols-3 gap-3 flex-1">
              {TOOLS.map((t, i) => {
                const disabled = !t.to;
                return (
                  <button
                    key={t.label}
                    disabled={disabled}
                    onClick={() => t.to && navigate(t.to)}
                    className={`rounded-2xl border p-3 flex flex-col items-center justify-center text-center gap-2 min-h-[92px] transition-all ${
                      disabled ? "border-dashed border-gray-200 bg-gray-50/50 cursor-default" : "border-gray-100 hover:border-[#D4AF37]/50 hover:shadow-[0_6px_18px_rgb(0,0,0,0.06)] hover:-translate-y-0.5"
                    }`}
                    data-testid={`home-tool-${i}`}
                  >
                    <t.icon size={22} style={{ color: t.color }} strokeWidth={1.8} />
                    <span className={`text-[11px] font-semibold leading-tight ${disabled ? "text-[#9aa1ab]" : "text-[#374151]"}`}>{t.label}</span>
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => navigate("/ai-resources")}
              className="mt-5 w-full inline-flex items-center justify-center gap-1.5 text-sm font-bold text-[#7c3aed] hover:text-[#5b21b6] transition-colors"
              data-testid="home-explore-tools-btn"
            >
              Explore All Tools <ArrowRight size={15} />
            </button>
          </Panel>

          {/* Learn on LaunchBS */}
          <Panel title="Learn on LaunchBS" subtitle="Learn the skills you need to build and scale your startup." className="bg-white border border-gray-100 shadow-[0_4px_24px_rgb(0,0,0,0.04)]">
            <div className="space-y-4 flex-1">
              {COURSES.map((c, i) => (
                <div key={c.name} className="flex items-center gap-3" data-testid={`home-course-${i}`}>
                  <span className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${c.tint}`}><c.icon size={18} /></span>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-sm text-[#0F0F0F] truncate">{c.name}</p>
                    <p className="text-[11px] text-[#9aa1ab]">{c.lessons} Lessons</p>
                    <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden mt-1.5">
                      <div className="h-full rounded-full transition-all duration-700" style={{ width: `${c.pct}%`, background: barColor(c.pct) }} />
                    </div>
                  </div>
                  <span className="text-xs font-bold text-[#687280] shrink-0">{c.pct}%</span>
                </div>
              ))}
            </div>
            <button
              onClick={() => navigate("/learn")}
              className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#0F0F0F] text-[#D4AF37] rounded-full py-3 text-sm font-bold hover:bg-[#1c1c1c] transition-colors"
              data-testid="home-view-courses-btn"
            >
              View All Courses <ArrowRight size={16} />
            </button>
          </Panel>
        </div>
      </section>
    </>
  );
};

export const GoProBanner = () => {
  const { isPro, openUpgrade } = usePro();
  if (isPro) return null;
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-8 pt-16" data-testid="home-gopro-banner">
      <div className="rounded-3xl bg-gradient-to-r from-[#FBF7EA] to-[#FDF6E3] border border-[#D4AF37]/30 p-6 lg:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-5">
        <span className="w-14 h-14 rounded-2xl bg-[#D4AF37] text-[#0F0F0F] flex items-center justify-center shrink-0 shadow-[0_8px_24px_rgba(212,175,55,0.4)]">
          <Crown size={26} />
        </span>
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-2xl font-extrabold text-[#0F0F0F]">Unlock Your Full Potential</h3>
          <p className="text-[#687280] mt-1">Go Pro and get unlimited access to all premium tools, courses, and advanced AI features.</p>
        </div>
        <button
          onClick={() => openUpgrade("default")}
          className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0F0F0F] rounded-full px-7 py-3.5 text-sm font-bold hover:bg-[#c5a030] transition-colors shrink-0"
          data-testid="home-upgrade-btn"
        >
          <Crown size={17} /> Upgrade Now
        </button>
      </div>
    </section>
  );
};
